import DomPurify from "isomorphic-dompurify";
import { CornerRightDown } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button, ButtonVariantTypes, getButtonVariantStylesAndSize } from "components/ui/button";
import { FadeIn } from "components/ui/FadeIn";

const ImageText = ({
  alt,
  src,
  title,
  subtitle,
  buttonLabel,
  body,
  buttonURL,
  miniTitle,
  showLearn = false,
  miniTitleColor,
  bodyTextColor,
  titleTag = "h1",
  subHeaderTag = "h2",
  titleTextColor = "#073937",
  fullOverlay = false,
  subtitleTextColor = "#073937",
  bgColor = "",
  glowEffect = false,
  isTop = false,
  isTall = false,
  darkGradient = false
}: {
  alt: string;
  src: string;
  miniTitle?: string;
  body?: string;
  bodyTextColor?: string;
  miniTitleColor?: string;
  title: string;
  subtitle: string;
  buttonLabel: string;
  buttonURL: string;
  showLearn?: boolean;
  titleTag: React.ElementType;
  subHeaderTag: React.ElementType;
  bgColor?: string;
  titleTextColor?: string;
  subtitleTextColor?: string;
  glowEffect?: boolean;
  isTop?: boolean;
  fullOverlay?: boolean;
  isTall?: boolean;
  darkGradient?: boolean;
}) => {
  const TitleTag = titleTag;
  const SubHeaderTag = subHeaderTag;

  return (
    <section
      id="ImageText-8"
      style={{ background: bgColor }}
      className={`${isTop ? "intro-image -mt-[82px]" : ""} intro-image relative @container`}
    >
      {isTop && <div className={`bnbb ${fullOverlay ? "lgs" : ""}`}></div>}
      <div className={`relative ${isTall ? "h-[90vh]" : "h-[750px]"} w-full`}>
        <div className={`relative ${isTall ? "h-[90vh]" : "h-[750px]"}`}>
          {src && (
            <Image
              src={src}
              alt={alt}
              quality={90}
              fill
              priority
              style={{
                objectFit: "cover",
                objectPosition: "50% 62%"
              }}
            />
          )}
          {darkGradient && (
            <div
              className="bg-gradient-to-b absolute inset-0 from-transparent to-black opacity-60"
              style={{ zIndex: 10 }}
            ></div>
          )}
          <div className="relative z-20 m-auto flex h-full items-center justify-center px-6 py-20 @2xl:px-28 @2xl:py-12">
            <FadeIn className="mx-auto max-w-[600px] text-center">
              {miniTitle && (
                <p className="mb-4 text-center text-[18px] font-semibold text-white" style={{ color: miniTitleColor }}>
                  {miniTitle}
                </p>
              )}
              <TitleTag
                className="mb-6 text-center font-heading text-[42px] leading-[48px] [text-wrap:balance] lg:text-[52px] lg:leading-[64px]"
                style={{ color: darkGradient ? "white" : titleTextColor }}
                dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title) }}
              />
              <SubHeaderTag
                className={`${
                  glowEffect ? "smallglow" : ""
                } relative mx-auto mb-12 mt-6 max-w-3xl px-12 text-lg leading-8`}
                style={{ color: darkGradient ? "white" : subtitleTextColor }}
                dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subtitle) }}
              />
              {body && (
                <p
                  className="mb-8 text-center text-md [text-wrap:balance] lg:text-lg"
                  style={{ color: darkGradient ? "white" : bodyTextColor }}
                >
                  {body}
                </p>
              )}
              {buttonLabel && buttonURL && (
                <Link href={buttonURL} passHref>
                  <button
                    className={getButtonVariantStylesAndSize({ variant: ButtonVariantTypes.BRAND, size: "xl" })}
                    title={buttonLabel}
                  >
                    {buttonLabel}
                  </button>
                </Link>
              )}
              {showLearn && (
                <div className="mt-8 text-center">
                  <Button
                    variant={ButtonVariantTypes.LINK_ICON_ARROW}
                    className="ml-6 items-center justify-center"
                    icon={
                      <CornerRightDown size={20} className="corner-right-down-icon ml-2 mt-[10px]" strokeWidth={2} />
                    }
                  >
                    <Link href="#atom-layout-69"> Or Learn More</Link>
                  </Button>
                </div>
              )}
            </FadeIn>
          </div>
        </div>
      </div>
      <div className="hero--coverrrr relative z-0"></div>
    </section>
  );
};

export default ImageText;
